import {
  X,
  Plus,
  Edit2,
  Trash2,
  Search,
  RefreshCw,
  Upload,
} from "lucide-react";
import { useState, useEffect } from "react";
import React from "react";
import {
  getAllPresetsAPI,
  createPresetAPI,
  updatePresetAPI,
  deletePresetAPI,
} from "../../services/api";
import api from "../../services/api";
import { activeCurrencySymbol } from "../../utils/currency";

// Type Definitions
interface Choice {
  label: string;
  price: number;
  default?: boolean;
  image?: string;
}

interface PortionOption {
  key: string;
  label: string;
  icon?: string;
}

interface CustomizationOption {
  name: string;
  required: boolean;
  multiple: boolean;
  customized?: boolean;
  multipleAddons?: boolean;
  choices: Choice[];
  noOfFreeToppings?: number;
  maxToppingsAllowed?: number;
  minAddons?: number;
  displayType?: "checkbox" | "quantity" | "portion" | "radio";
  quantityOptions?: number[];
  portionOptions?: PortionOption[];
  showPortionSelector?: boolean;
}

interface PresetDraft {
  name: string;
  customizationOptions: CustomizationOption[];
}

interface Preset {
  _id: string;
  name: string;
  customizationOptions: CustomizationOption[];
  isActive: boolean;
}

export default function AddPreset() {
  const [showAddPreset, setShowAddPreset] = useState<boolean>(false);
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [presetErrors, setPresetErrors] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 6;

  const [presetDraft, setPresetDraft] = useState<PresetDraft>({
    name: "",
    customizationOptions: [
      {
        name: "",
        required: false,
        multiple: false,
        customized: true,
        multipleAddons: false,
        choices: [{ label: "", price: 0, default: false }],
        displayType: "checkbox",
        quantityOptions: [0, 1, 2, 3],
        portionOptions: [
          { key: "left", label: "Left" },
          { key: "whole", label: "Whole" },
          { key: "right", label: "Right" },
        ],
        showPortionSelector: false,
      },
    ],
  });

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await getAllPresetsAPI();
      const presetsData = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setPresets(presetsData);
    } catch (error) {
      console.error("Failed to fetch presets:", error);
      setPresets([]);
    } finally {
      setLoading(false);
    }
  };

  const validatePresetDraft = (): boolean => {
    const errors: string[] = [];

    if (!presetDraft.name.trim()) {
      errors.push("Preset name is required");
    }

    const option = presetDraft.customizationOptions[0];

    if (!option.name.trim()) {
      errors.push("Group name is required");
    }

    const validChoices = option.choices.filter((c) => c.label.trim());
    if (validChoices.length === 0) {
      errors.push("At least one choice with a label is required");
    }

    if (errors.length > 0) {
      setPresetErrors(errors);
      return false;
    }

    setPresetErrors([]);
    return true;
  };

  const handleChoiceImageUpload = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("image", file);
    const response = await api.post<{ url: string }>("/upload-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.url;
  };

  const handleSavePreset = async (): Promise<void> => {
    if (!validatePresetDraft()) return;

    const dataToSend = {
      name: presetDraft.name,
      customizationOptions: presetDraft.customizationOptions.map((option) => ({
        name: option.name,
        required: option.required,
        multiple: option.multiple,
        customized: option.customized || false,
        choices: option.choices
          .filter((c) => c.label.trim())
          .map((c) => ({
            label: c.label,
            price: Number(c.price) || 0,
            default: c.default || false,
            ...(c.image && { image: c.image }),
          })),
        noOfFreeToppings: option.noOfFreeToppings,
        maxToppingsAllowed: option.maxToppingsAllowed,
        minAddons: option.minAddons,
        multipleAddons: option.multipleAddons || false,
        displayType: option.displayType || "checkbox",
        quantityOptions: option.quantityOptions || [0, 1, 2, 3],
        portionOptions: option.portionOptions || [
          { key: "left", label: "Left" },
          { key: "whole", label: "Whole" },
          { key: "right", label: "Right" },
        ],
        showPortionSelector: option.showPortionSelector || false,
      })),
    };

    try {
      setLoading(true);
      if (editingPresetId !== null) {
        await updatePresetAPI(editingPresetId, dataToSend);
      } else {
        await createPresetAPI(dataToSend);
      }
      setShowAddPreset(false);
      setEditingPresetId(null);
      resetForm();
      fetchPresets();
    } catch (error) {
      console.error("Failed to save preset:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPreset = (preset: Preset): void => {
    setPresetDraft({
      name: preset.name,
      customizationOptions: preset.customizationOptions,
    });
    setEditingPresetId(preset._id);
    setShowAddPreset(true);
  };

  const handleDeletePreset = async (id: string): Promise<void> => {
    if (!window.confirm("Are you sure you want to delete this preset?")) return;
    try {
      await deletePresetAPI(id);
      fetchPresets();
    } catch (error) {
      console.error("Failed to delete preset:", error);
    }
  };

  const resetForm = (): void => {
    setPresetDraft({
      name: "",
      customizationOptions: [
        {
          name: "",
          required: false,
          multiple: false,
          customized: true,
          multipleAddons: false,
          choices: [{ label: "", price: 0, default: false }],
          noOfFreeToppings: undefined,
          maxToppingsAllowed: undefined,
          minAddons: undefined,
          displayType: "checkbox",
          quantityOptions: [0, 1, 2, 3],
          portionOptions: [
            { key: "left", label: "Left" },
            { key: "whole", label: "Whole" },
            { key: "right", label: "Right" },
          ],
          showPortionSelector: false,
        },
      ],
    });
    setPresetErrors([]);
    setEditingPresetId(null);
  };

  const handleOpenModal = (): void => {
    resetForm();
    setShowAddPreset(true);
  };

  const currentOption = presetDraft.customizationOptions[0];

  const filteredPresets = presets.filter((preset) =>
    preset.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredPresets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPresets = filteredPresets.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="orders-container max-w-7xl mx-auto h-screen p-4 pt-0 lg:p-8 flex flex-col overscroll-y-auto no-scrollbar relative">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            Customization Presets
          </h1>
          <button
            onClick={handleOpenModal}
            className="w-auto self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={18} />
            Add Preset
          </button>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="rounded-lg mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Presets
          </label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by preset name..."
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={fetchPresets}
              className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Refresh Presets"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= CONTENT ================= */}
      <div className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              Loading presets...
            </div>
          </div>
        ) : filteredPresets.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No presets found</p>
              <p className="text-sm mt-1">
                {searchQuery ? "Try a different search term" : "Add your first preset to get started"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {paginatedPresets.map((preset) => (
              <div
                key={preset._id}
                className="bg-white border rounded-lg p-3 shadow-sm hover:shadow transition flex h-full flex-col"
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-blue-600 font-semibold text-sm">
                    {preset.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditPreset(preset)}
                      className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeletePreset(preset._id)}
                      className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex-1">
                  {preset.customizationOptions.map((option, idx) => (
                    <div key={idx} className="mb-2">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <p className="text-xs font-medium text-gray-700 m-0">
                          {option.name}
                        </p>
                        {option.required && (
                          <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                            Required
                          </span>
                        )}
                        {option.multiple && (
                          <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                            Multiple
                          </span>
                        )}
                        {option.customized && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            0,1,2
                          </span>
                        )}
                        {option.multipleAddons && (
                          <span className="text-[10px] bg-teal-100 text-teal-700 px-2 py-0.5 rounded">
                            Multi-Addon
                          </span>
                        )}
                        {(option.minAddons ?? 0) > 0 && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            Min: {option.minAddons}
                          </span>
                        )}
                        {option.maxToppingsAllowed !== undefined && (
                          <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            Max: {option.maxToppingsAllowed}
                          </span>
                        )}
                        {(option.noOfFreeToppings ?? 0) > 0 && (
                          <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            Free: {option.noOfFreeToppings}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        {option.choices.map((choice, cIdx) => (
                          <div
                            key={cIdx}
                            className="flex items-center justify-between text-xs text-gray-600 pl-3"
                          >
                            <div className="flex items-center gap-2">
                              {choice.image && (
                                <img
                                  src={choice.image}
                                  alt={choice.label}
                                  className="w-5 h-5 rounded object-cover"
                                />
                              )}
                              <span>{choice.label}</span>
                              {choice.default && (
                                <span className="text-[10px] text-green-600">(default)</span>
                              )}
                            </div>
                            {choice.price > 0 && (
                              <span className="text-gray-500">+{activeCurrencySymbol()}{choice.price.toFixed(2)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-auto border-t pt-2">
                  <span className={`text-xs ${preset.isActive ? "text-green-600" : "text-gray-500"}`}>
                    {preset.isActive ? "● Active" : "○ Inactive"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {filteredPresets.length > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center">
          <div className="flex gap-2 items-center">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 rounded transition ${
                  currentPage === page ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showAddPreset && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => { setShowAddPreset(false); resetForm(); }}
          />

          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[95dvh] sm:max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="border-b px-4 sm:px-6 py-4 flex justify-between items-center gap-3">
              <h3 className="text-lg font-semibold">
                {editingPresetId !== null ? "Edit Customization Preset" : "Add Customization Preset"}
              </h3>
              <button onClick={() => { setShowAddPreset(false); resetForm(); }}>
                <X size={18} />
              </button>
            </div>

            {/* Body - Scrollable */}
            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
              {/* Error Display */}
              {presetErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm">
                  {presetErrors.map((err, i) => <p key={i}>{err}</p>)}
                </div>
              )}

              {/* Preset Name */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Preset Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full p-2.5 border rounded-md"
                  placeholder="e.g., Bake Level, Size Options"
                  value={presetDraft.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPresetDraft({ ...presetDraft, name: e.target.value })
                  }
                />
              </div>

              {/* Group Name */}
              <input
                className="w-full p-2.5 border rounded-md"
                placeholder="Group Name (e.g. Size, Cheese)"
                value={currentOption.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const updated = [...presetDraft.customizationOptions];
                  updated[0].name = e.target.value;
                  setPresetDraft({ ...presetDraft, customizationOptions: updated });
                }}
              />

              {/* Option Settings */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-8">
                {/* Required */}
                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentOption.required}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const updated = [...presetDraft.customizationOptions];
                        updated[0].required = e.target.checked;
                        updated[0].multiple = e.target.checked ? false : updated[0].multiple;
                        updated[0].customized = e.target.checked ? false : updated[0].customized;
                        updated[0].multipleAddons = e.target.checked ? false : updated[0].multipleAddons;
                        setPresetDraft({ ...presetDraft, customizationOptions: updated });
                      }}
                    />
                    Required
                  </label>
                  <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999] w-56 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg">
                    Allows user to select <b>only one</b> option from choices
                  </div>
                </div>

                {/* Multiple */}
                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentOption.multiple || false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const updated = [...presetDraft.customizationOptions];
                        updated[0].multiple = e.target.checked;
                        updated[0].required = e.target.checked ? false : updated[0].required;
                        updated[0].customized = e.target.checked ? false : updated[0].customized;
                        updated[0].multipleAddons = e.target.checked ? false : updated[0].multipleAddons;
                        setPresetDraft({ ...presetDraft, customizationOptions: updated });
                      }}
                    />
                    Multiple Choice
                  </label>
                  <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999] w-64 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg">
                    Allows user to select <b>multiple</b> options from choices
                  </div>
                </div>

                {/* 0,1,2 Customization */}
                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentOption.customized || false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const updated = [...presetDraft.customizationOptions];
                        updated[0].customized = e.target.checked;
                        updated[0].required = e.target.checked ? false : updated[0].required;
                        updated[0].multiple = e.target.checked ? false : updated[0].multiple;
                        updated[0].multipleAddons = e.target.checked ? false : updated[0].multipleAddons;
                        setPresetDraft({ ...presetDraft, customizationOptions: updated });
                      }}
                    />
                    0,1,2 Customization
                  </label>
                  <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999] w-72 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg">
                    If any option is not selected, allows user to choose the{" "}
                    <b>quantity/count</b> of a particular choice
                  </div>
                </div>

                {/* Multiple Addons */}
                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentOption.multipleAddons || false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const updated = [...presetDraft.customizationOptions];
                        updated[0].multipleAddons = e.target.checked;
                        updated[0].required = e.target.checked ? false : updated[0].required;
                        updated[0].multiple = e.target.checked ? false : updated[0].multiple;
                        updated[0].customized = e.target.checked ? false : updated[0].customized;
                        setPresetDraft({ ...presetDraft, customizationOptions: updated });
                      }}
                    />
                    Multiple Addons
                  </label>
                  <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999] w-72 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg">
                    Each choice gets its own <b>- qty +</b> stepper. Use for items like dips where the user can add multiple of the same item.
                  </div>
                </div>
              </div>

              {/* Addon Limits */}
              {!currentOption.required && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="text-sm font-semibold mb-3">Addon Limits</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Min Addons
                        <span className="text-gray-400 ml-1">(0 = optional)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        className="w-full p-2 border rounded-md text-sm"
                        value={currentOption.minAddons ?? ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const updated = [...presetDraft.customizationOptions];
                          updated[0].minAddons = e.target.value !== "" ? parseInt(e.target.value, 10) : undefined;
                          setPresetDraft({ ...presetDraft, customizationOptions: updated });
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Max Addons
                        <span className="text-gray-400 ml-1">(blank = unlimited)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="unlimited"
                        className={`w-full p-2 border rounded-md text-sm ${
                          currentOption.maxToppingsAllowed !== undefined &&
                          currentOption.noOfFreeToppings !== undefined &&
                          currentOption.maxToppingsAllowed <= currentOption.noOfFreeToppings
                            ? "border-red-400"
                            : "border-gray-300"
                        }`}
                        value={currentOption.maxToppingsAllowed ?? ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const updated = [...presetDraft.customizationOptions];
                          updated[0].maxToppingsAllowed = e.target.value !== "" ? parseInt(e.target.value, 10) : undefined;
                          setPresetDraft({ ...presetDraft, customizationOptions: updated });
                        }}
                      />
                      {currentOption.maxToppingsAllowed !== undefined &&
                        currentOption.noOfFreeToppings !== undefined &&
                        currentOption.maxToppingsAllowed <= currentOption.noOfFreeToppings && (
                          <p className="text-xs text-red-500 mt-1">Must be greater than free items</p>
                        )}
                    </div>
                    {!currentOption.multiple && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Free Items
                          <span className="text-gray-400 ml-1">(included free)</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          placeholder="0"
                          className="w-full p-2 border rounded-md text-sm"
                          value={currentOption.noOfFreeToppings ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const updated = [...presetDraft.customizationOptions];
                            updated[0].noOfFreeToppings = e.target.value !== "" ? parseInt(e.target.value, 10) : undefined;
                            setPresetDraft({ ...presetDraft, customizationOptions: updated });
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Display Settings */}
              {currentOption.customized && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="text-sm font-semibold mb-3">Display Settings</h4>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity Options (comma separated numbers)
                    </label>
                    <input
                      className="w-full p-2.5 border rounded-md"
                      placeholder="e.g., 0, 1, 2, 3 or -1, 0, 1, 2"
                      value={(currentOption.quantityOptions || []).join(", ")}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const values = e.target.value
                          .split(",")
                          .map((v) => parseInt(v.trim()))
                          .filter((v) => !isNaN(v));
                        const updated = [...presetDraft.customizationOptions];
                        updated[0].quantityOptions = values.length > 0 ? values : [0, 1, 2, 3];
                        setPresetDraft({ ...presetDraft, customizationOptions: updated });
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      For default toppings use -1, 0, 1, 2 (where -1 means "none", 0 is default). For extras use 0, 1, 2, 3.
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={currentOption.showPortionSelector || false}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const updated = [...presetDraft.customizationOptions];
                          updated[0].showPortionSelector = e.target.checked;
                          setPresetDraft({ ...presetDraft, customizationOptions: updated });
                        }}
                      />
                      Show Portion Selector (Left/Right/Whole for Half & Half pizzas)
                    </label>
                  </div>

                  {currentOption.showPortionSelector && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Portion Options
                      </label>
                      <div className="space-y-2">
                        {(currentOption.portionOptions || []).map((option, idx) => (
                          <div key={idx} className="flex flex-col sm:flex-row gap-2 sm:items-center">
                            <input
                              className="flex-1 p-2 border rounded-md text-sm"
                              placeholder="Key (e.g., left)"
                              value={option.key}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updated = [...presetDraft.customizationOptions];
                                const portionOpts = [...(updated[0].portionOptions || [])];
                                portionOpts[idx] = { ...portionOpts[idx], key: e.target.value };
                                updated[0].portionOptions = portionOpts;
                                setPresetDraft({ ...presetDraft, customizationOptions: updated });
                              }}
                            />
                            <input
                              className="flex-1 p-2 border rounded-md text-sm"
                              placeholder="Label (e.g., Left Half)"
                              value={option.label}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updated = [...presetDraft.customizationOptions];
                                const portionOpts = [...(updated[0].portionOptions || [])];
                                portionOpts[idx] = { ...portionOpts[idx], label: e.target.value };
                                updated[0].portionOptions = portionOpts;
                                setPresetDraft({ ...presetDraft, customizationOptions: updated });
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...presetDraft.customizationOptions];
                                updated[0].portionOptions = (updated[0].portionOptions || []).filter((_, i) => i !== idx);
                                setPresetDraft({ ...presetDraft, customizationOptions: updated });
                              }}
                              className="text-red-600 text-sm px-2"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...presetDraft.customizationOptions];
                            updated[0].portionOptions = [...(updated[0].portionOptions || []), { key: "", label: "" }];
                            setPresetDraft({ ...presetDraft, customizationOptions: updated });
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          + Add Portion Option
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Choices Table */}
              <div className="border rounded-lg overflow-x-auto">
                <table className="min-w-[700px] sm:min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Choice</th>
                      <th className="px-4 py-2 text-left">Price</th>
                      <th className="px-4 py-2 text-center">Default</th>
                      <th className="px-4 py-2 text-left">Image</th>
                      <th className="px-4 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {currentOption.choices.map((choice, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2">
                          <input
                            className="w-full border p-2 rounded"
                            placeholder="Label"
                            value={choice.label}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const updated = [...presetDraft.customizationOptions];
                              updated[0].choices[index].label = e.target.value;
                              setPresetDraft({ ...presetDraft, customizationOptions: updated });
                            }}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-full border p-2 rounded"
                            placeholder="0.00"
                            value={choice.price === 0 ? "" : choice.price}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const val = e.target.value;
                              const updated = [...presetDraft.customizationOptions];
                              updated[0].choices[index].price = val === "" ? 0 : parseFloat(val) || 0;
                              setPresetDraft({ ...presetDraft, customizationOptions: updated });
                            }}
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={choice.default || false}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const updated = [...presetDraft.customizationOptions];
                              if (e.target.checked && currentOption.required) {
                                updated[0].choices.forEach((c, i) => { c.default = i === index; });
                              } else {
                                updated[0].choices[index].default = e.target.checked;
                              }
                              setPresetDraft({ ...presetDraft, customizationOptions: updated });
                            }}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <label className="block">
                            <input
                              type="file"
                              accept="image/png, image/jpeg, image/webp"
                              className="hidden"
                              onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  const url = await handleChoiceImageUpload(file);
                                  const updated = [...presetDraft.customizationOptions];
                                  updated[0].choices[index].image = url;
                                  setPresetDraft({ ...presetDraft, customizationOptions: updated });
                                } catch (err) {
                                  console.error("Image upload failed", err);
                                }
                              }}
                            />
                            {!choice.image ? (
                              <div className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 rounded-md p-3 text-xs text-gray-500 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
                                <Upload size={16} className="text-blue-500" />
                                <span>Upload</span>
                              </div>
                            ) : (
                              <div className="relative cursor-pointer w-12 h-12 group">
                                <img
                                  src={choice.image}
                                  alt="choice"
                                  className="w-full h-full object-cover rounded-md border"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-md transition">
                                  <span className="text-[10px] text-white">Change</span>
                                </div>
                              </div>
                            )}
                          </label>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => {
                              const updated = [...presetDraft.customizationOptions];
                              updated[0].choices.splice(index, 1);
                              setPresetDraft({ ...presetDraft, customizationOptions: updated });
                            }}
                            className="text-red-600 text-sm"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                onClick={() => {
                  const lastChoice = currentOption.choices[currentOption.choices.length - 1];
                  if (lastChoice && !lastChoice.label.trim()) {
                    setPresetErrors(["Please fill the previous choice before adding a new one."]);
                    return;
                  }
                  const updated = [...presetDraft.customizationOptions];
                  updated[0].choices.push({ label: "", price: 0, default: false });
                  setPresetDraft({ ...presetDraft, customizationOptions: updated });
                  setPresetErrors([]);
                }}
                className="w-full py-2 bg-gray-100 rounded-md hover:bg-gray-200 transition"
              >
                + Add Choice
              </button>
            </div>

            {/* Footer */}
            <div className="border-t px-4 sm:px-6 py-4 flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowAddPreset(false); resetForm(); }}
                className="w-full sm:w-auto px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePreset}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              >
                {loading ? "Saving..." : "Save Preset"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
